import { attachmentManagementAdmissionErrorMessage } from "./useAppBindings";
import { useCallback, useState } from "react";
import { ApiError, apiClient, apiErrorMessage } from "@/api/client";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";

type Schemas = publicOperatorAdmin.components["schemas"];
export type RestorePlan = Schemas["AttachmentEnvironmentRestorePlanPreviewRequest"];
export type RestorePreview = Schemas["AttachmentEnvironmentRestorePlanPreviewResponse"];
export type RestoreUpload = Schemas["AttachmentRecoveryImportResponse"];

export type RestoreSourceIdFields = Schemas["AttachmentEnvironmentRestoreSourceRecordIdFieldsResponse"];
const restoreMessages: Record<string, string> = {
  asynchronous_execution_preview_stale: "確認後に設定が変わりました。復元内容を確認し直してください。",
  attachment_environment_restore_source_invalid: "復元元のStorage・フォルダ・レコードIDの選択を確認してください。",
  attachment_environment_restore_source_authorization_revoked: "現在の復元元へのアクセス権限を確認できません。組織の所属とStorageの権限を確認してください。",
  attachment_environment_restore_source_unavailable: "復元元のStorageを利用できません。現在の登録状態を確認してください。",
  attachment_recovery_archive_zip_invalid: "復元情報ファイルの形式が正しくありません。移行元で出力したZIPを選択してください。",
  attachment_recovery_archive_signature_invalid: "復元情報ファイルの署名を確認できません。内容を変更せず移行元から出力し直してください。",
  attachment_recovery_import_authorization_revoked: "現在の復元情報取込権限を確認できません。組織の所属状態を確認してください。",
  attachment_recovery_import_target_changed: "取込中に移行先設定が変わりました。現在の設定を確認してやり直してください。",
  attachment_environment_restore_info_source_record_id_field_not_found: "元レコードID列が見つかりません。列を再取得して選び直してください。",
  attachment_environment_restore_info_source_record_id_field_type_invalid: "元レコードID列には文字列（1行）または数値を選択してください。",
  attachment_environment_restore_info_invalid: "復元情報ファイルの形式が正しくありません。移行元で出力したZIPを選択してください。",
  attachment_environment_restore_info_signature_invalid: "復元情報ファイルの署名を確認できません。内容を変更せず、移行元から出力し直してください。",
  attachment_environment_restore_info_expired: "復元情報の参照が期限切れ、または一致しません。ファイルを選び直してください。",
  attachment_environment_restore_info_scope_invalid: "復元情報またはフィールドの対応付けが現在の設定と一致しません。移行元と移行先の設定を確認してください。",
  attachment_environment_restore_info_source_unavailable: "復元に必要な情報を取得できません。接続状態を確認して再試行してください。",
};
export function restoreErrorMessage(error: unknown) {
  const admission = attachmentManagementAdmissionErrorMessage(error);
  if (admission) return admission;
  if (error instanceof SyntaxError) return restoreMessages.attachment_environment_restore_info_invalid;
  if (error instanceof ApiError) {
    const message = restoreMessages[error.code] ?? `環境移行復元の操作に失敗しました。（${error.code}）`;
    return error.requestId ? `${message}（requestId: ${error.requestId}）` : message;
  }
  return apiErrorMessage(error, "環境移行復元の操作に失敗しました。");
}

export function useAttachmentEnvironmentRestore(kintoneAppId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useCallback(async <T,>(suffix: string, body: unknown): Promise<T> => {
    setIsLoading(true);
    setError(null);
    try {
      return await apiClient<T>(`/kintone-apps/${kintoneAppId}/attachment-environment-restore-${suffix}`, {
        method: "POST", body: JSON.stringify(body),
      });
    } catch (err) {
      setError(restoreErrorMessage(err));
      throw err;
    } finally { setIsLoading(false); }
  }, [kintoneAppId]);
  const previewResult = useCallback((executionId: string) => apiClient<Schemas["AttachmentEnvironmentRestoreCompletedPreview"]>(
    `/kintone-apps/${kintoneAppId}/attachment-environment-restore-previews/${executionId}`,
  ), [kintoneAppId]);
  const clearError = useCallback(() => setError(null), []);
  const sourceRecordIdFields = useCallback(() => apiClient<RestoreSourceIdFields>(
    `/kintone-apps/${kintoneAppId}/attachment-environment-restore-source-record-id-fields`,
  ), [kintoneAppId]);
  return {
    isLoading, error, clearError, sourceRecordIdFields, previewResult,
    exportInfo: () => request<Schemas["AsynchronousExecutionResponse"]>("info-exports", {}),
    uploadInfo: async (file: File) => {
      setIsLoading(true); setError(null);
      try { return await apiClient<RestoreUpload>(`/kintone-apps/${kintoneAppId}/attachment-environment-restore-info-uploads`, {
        method: "POST", headers: { "content-type": "application/zip" }, body: file,
      }); } catch (err) { setError(restoreErrorMessage(err)); throw err; } finally { setIsLoading(false); }
    },
    preview: (body: RestorePlan) => request<RestorePreview | Schemas["AsynchronousExecutionResponse"]>("plan-preview", body),
    start: (body: Schemas["AttachmentEnvironmentRestoreExecutionStartRequest"]) => request<Schemas["AsynchronousExecutionResponse"]>("executions", body),
  };
}
